import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaCreditCard, FaMobile, FaSpinner, FaTimes, FaCheckCircle } from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import api from '../../utils/api';
import subscriptionConfig from '../../config/subscription';
import { Helmet } from 'react-helmet-async';

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { showToast } = useToast();
  
  // Get subscription data from location state or URL parameters
  const searchParams = new URLSearchParams(location.search);
  const selectedPlan = location.state?.selectedPlan || searchParams.get('tier');
  const billingCycle = location.state?.billingCycle || searchParams.get('billing') || 'quarterly';
  
  // States
  const [paymentMethod, setPaymentMethod] = useState('mpesa');
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber || '');
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    cardHolder: '',
    expiryDate: '',
    cvv: ''
  });
  const [mpesaProcessing, setMpesaProcessing] = useState(false);
  const [transactionId, setTransactionId] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  
  // Discount code states
  const [discountCode, setDiscountCode] = useState('');
  const [discountError, setDiscountError] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  
  useEffect(() => {
    // Redirect if no subscription plan is selected
    if (!selectedPlan && !redirecting) {
      setRedirecting(true);
      showToast('Please select a subscription plan first', 'error');
      
      // Determine where to redirect based on user role
      let redirectPath = '/subscriptions';
      if (user) {
        if (user.role === 'parent') {
          redirectPath = '/parent/subscription';
        } else if (user.role === 'school_admin') {
          redirectPath = '/school/subscription';
        } else if (user.role === 'mentor') {
          redirectPath = '/mentor/subscription';
        }
      }
      
      navigate(redirectPath);
    }
  }, [selectedPlan, navigate, showToast, user, redirecting]);
  
  // Get pricing based on billing cycle
  const getPrice = () => {
    if (!selectedPlan) return 0;
    
    const plans = user?.role === 'school_admin' 
      ? subscriptionConfig.schoolSubscriptions 
      : subscriptionConfig.parentSubscriptions;
      
    const plan = plans[selectedPlan];
    
    if (!plan) return 0;
    
    if (billingCycle === 'annual') {
      // Remove currency symbol and parse
      const priceText = plan.price.annual;
      if (priceText === 'Free') return 0;
      
      const priceValue = parseFloat(priceText.replace(/[^0-9]/g, ''));
      return priceValue || 0;
    } else {
      // For quarterly billing
      const priceText = plan.price.quarterly;
      if (priceText === 'Free') return 0;
      
      const priceValue = parseFloat(priceText.replace(/[^0-9]/g, ''));
      return priceValue || 0;
    }
  };
  
  // Calculate final price after discount
  const getFinalPrice = () => {
    const originalPrice = getPrice();
    
    if (!appliedDiscount) return originalPrice;
    
    // Apply the discount percentage
    const discountAmount = originalPrice * (appliedDiscount.percentage / 100);
    return originalPrice - discountAmount;
  };
  
  // Validate and apply discount code
  const validateDiscountCode = async () => {
    if (!discountCode.trim()) {
      setDiscountError('Please enter a discount code');
      return;
    }

    try {
      setIsValidatingCode(true);
      setDiscountError('');
      
      const response = await api.post('/discounts/validate', {
        code: discountCode,
        amount: getPrice(),
        subscriptionTier: selectedPlan,
        billingCycle
      });

      if (response.data.success) {
        setAppliedDiscount({
          code: response.data.data.code,
          percentage: response.data.data.discount
        });
        showToast(`Discount code applied: ${response.data.data.discount}% off`, 'success');
      } else {
        setDiscountError(response.data.message || 'Invalid discount code');
        setAppliedDiscount(null);
      }
    } catch (error) {
      console.error('Error validating discount code:', error);
      setDiscountError(error.response?.data?.message || 'Error validating discount code');
      setAppliedDiscount(null);
    } finally {
      setIsValidatingCode(false);
    }
  };

  // Remove applied discount
  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
    setDiscountError('');
  };
  
  // Handle card input changes
  const handleCardInputChange = (e) => {
    const { name, value } = e.target;
    setCardDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Format card number with spaces
  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    
    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  };
  
  // Handle card number formatting
  const handleCardNumberChange = (e) => {
    const value = formatCardNumber(e.target.value);
    setCardDetails(prev => ({
      ...prev,
      cardNumber: value
    }));
  };
  
  // Handle expiry date formatting (MM/YY)
  const handleExpiryChange = (e) => {
    let { value } = e.target;
    value = value.replace(/\D/g, '');
    
    if (value.length > 2) {
      value = `${value.slice(0, 2)}/${value.slice(2, 4)}`;
    }
    
    setCardDetails(prev => ({
      ...prev,
      expiryDate: value
    }));
  };
  
  // Handle phone number input change
  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    setPhoneNumber(value);
    
    // Basic validation
    if (value && value.length < 10) {
      setPhoneNumberError('Phone number should be at least 10 digits');
    } else {
      setPhoneNumberError('');
    }
  };
  
  // Process payment with selected method
  const processPayment = async () => {
    if (!selectedPlan) {
      showToast('Please select a subscription plan first', 'error');
      return;
    }
    
    setLoading(true);
    
    try {
      if (paymentMethod === 'mpesa') {
        await processMpesaPayment();
      } else if (paymentMethod === 'card') {
        await processCardPayment();
      }
    } catch (error) {
      console.error('Payment error:', error);
      showToast(error.response?.data?.message || 'Payment processing failed', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Process M-Pesa payment
  const processMpesaPayment = async () => {
    // Validate phone number
    if (!phoneNumber || phoneNumber.length < 10) {
      showToast('Please enter a valid phone number', 'error');
      setLoading(false);
      return;
    }
    
    // Don't proceed if there's a validation error
    if (phoneNumberError) {
      showToast(phoneNumberError, 'error');
      setLoading(false);
      return;
    }

    // Check if amount is 0 (could happen with full discount)
    const paymentAmount = getFinalPrice();
    if (paymentAmount <= 0) {
      try {
        // For school admin users, we need to fetch the school ID
        let schoolId = null;
        if (user?.role === 'school_admin') {
          // Fetch the school associated with this admin
          try {
            // First let's try to get the school ID from the user's dashboard data
            const dashboardResponse = await api.get('/schools/dashboard');
            console.log('School dashboard response:', dashboardResponse.data);
            
            if (dashboardResponse.data.success) {
              // Try to extract the school ID from various possible locations
              const schoolData = dashboardResponse.data.data;
              const schoolInfo = schoolData.schoolInfo;
              
              // Try to get the ID from multiple possible fields
              if (schoolInfo && schoolInfo._id) {
                schoolId = schoolInfo._id;
                console.log('Found school ID from schoolInfo._id:', schoolId);
              } else if (schoolInfo && schoolInfo.schoolId) {
                schoolId = schoolInfo.schoolId;
                console.log('Found school ID from schoolInfo.schoolId:', schoolId);
              }
            }
          } catch (error) {
            console.error('Error fetching school dashboard:', error);
            showToast('Could not retrieve your school information', 'error');
            setMpesaProcessing(false);
            setLoading(false);
            return;
          }
        }
        
        // Handle free subscriptions without M-Pesa
        const response = await api.post('/subscriptions', {
          tier: selectedPlan,
          billingCycle,
          paymentMethod: 'free',
          discountCode: appliedDiscount ? appliedDiscount.code : null,
          ...(schoolId && { schoolId }) // Include schoolId only if it's available
        });
        
        showToast('Your free subscription is now active!', 'success');
        
        // Get appropriate redirect path based on user role
        let redirectPath = '/dashboard';
        if (user) {
          if (user.role === 'parent') {
            redirectPath = '/parent';
          } else if (user.role === 'school_admin') {
            redirectPath = '/school';
          } else if (user.role === 'mentor') {
            redirectPath = '/mentor';
          }
        }
        
        navigate(redirectPath, { state: { subscriptionSuccess: true }});
        return;
      } catch (error) {
        console.error('Free subscription error:', error);
        showToast(error.response?.data?.error || 'Unable to activate free subscription', 'error');
        setLoading(false);
        return;
      }
    }
    
    try {
      setMpesaProcessing(true);
      
      // For school admin users, we need to fetch the school ID
      let schoolId = null;
      if (user?.role === 'school_admin') {
        // Fetch the school associated with this admin
        try {
          // First let's try to get the school ID from the user's dashboard data
          const dashboardResponse = await api.get('/schools/dashboard');
          console.log('School dashboard response:', dashboardResponse.data);
          
          if (dashboardResponse.data.success) {
            // Try to extract the school ID from various possible locations
            const schoolData = dashboardResponse.data.data;
            const schoolInfo = schoolData.schoolInfo;
            
            // Try to get the ID from multiple possible fields
            if (schoolInfo && schoolInfo._id) {
              schoolId = schoolInfo._id;
              console.log('Found school ID from schoolInfo._id:', schoolId);
            } else if (schoolInfo && schoolInfo.schoolId) {
              schoolId = schoolInfo.schoolId;
              console.log('Found school ID from schoolInfo.schoolId:', schoolId);
            }
          }
        } catch (error) {
          console.error('Error fetching school dashboard:', error);
          showToast('Could not retrieve your school information', 'error');
          setMpesaProcessing(false);
          setLoading(false);
          return;
        }
        
        // If we couldn't get the ID, try the profile endpoint
        if (!schoolId) {
          try {
            console.log('Trying profile endpoint as fallback...');
            const profileResponse = await api.get('/schools/profile');
            console.log('School profile response:', profileResponse.data);
            
            // Try to extract the school ID from the profile data
            if (profileResponse.data.success && profileResponse.data.data) {
              // Try to get the ID from multiple possible fields
              if (profileResponse.data.data._id) {
                schoolId = profileResponse.data.data._id;
                console.log('Found school ID from profile _id:', schoolId);
              } else if (profileResponse.data.data.schoolId) {
                schoolId = profileResponse.data.data.schoolId;
                console.log('Found school ID from profile schoolId:', schoolId);
              }
            }
          } catch (profileError) {
            console.error('Error fetching school profile:', profileError);
          }
        }
        
        // If we still couldn't get the ID, show an error
        if (!schoolId) {
          console.error('Could not find school ID in API responses');
          showToast('Could not determine your school information', 'error');
          setMpesaProcessing(false);
          setLoading(false);
          return;
        }
        
        console.log('Using school ID for subscription:', schoolId);
      }
      
      // Format phone number with proper validation and formatting
      // Phone number should be in format 254XXXXXXXXX (12 digits total)
      let formattedPhone = phoneNumber.trim().replace(/\s+/g, '');
      
      // Remove any non-digit characters
      formattedPhone = formattedPhone.replace(/\D/g, '');
      
      // Handle different formats of Kenyan numbers
      if (formattedPhone.startsWith('0')) {
        // Convert 07XXXXXXXX to 2547XXXXXXXX
        formattedPhone = '254' + formattedPhone.substring(1);
      } else if (formattedPhone.startsWith('7') || formattedPhone.startsWith('1')) {
        // Convert 7XXXXXXXX to 2547XXXXXXXX
        formattedPhone = '254' + formattedPhone;
      } else if (formattedPhone.startsWith('+254')) {
        // Remove the '+' from +254
        formattedPhone = formattedPhone.substring(1);
      } else if (!formattedPhone.startsWith('254')) {
        // If it doesn't start with 254, add it
        formattedPhone = '254' + formattedPhone;
      }
      
      // Final validation check
      if (formattedPhone.length !== 12 || !formattedPhone.startsWith('254')) {
        showToast('Invalid phone number format. Please enter a valid Kenyan phone number.', 'error');
        setMpesaProcessing(false);
        setLoading(false);
        return;
      }
      
      // Create a unique reference for this transaction
      const timestamp = new Date().getTime();
      const reference = `${user?.id || 'guest'}-${selectedPlan}-${timestamp}`;
      
      // Initiate STK Push
      const response = await api.post('/subscriptions/mpesa/stkpush', {
        phoneNumber: formattedPhone,
        amount: paymentAmount,
        reference: reference,
        tier: selectedPlan,
        billingCycle,
        discountCode: appliedDiscount ? appliedDiscount.code : null,
        ...(schoolId && { schoolId }) // Include schoolId only if it's available
      });
      
      console.log('MPesa STK Push Response:', response.data);
      
      // Extract checkout request ID from the response
      const checkoutRequestId = response.data.data.checkoutRequestID;
      setTransactionId(checkoutRequestId);
      
      // Poll for transaction status
      pollTransactionStatus(checkoutRequestId);
      
      showToast('M-Pesa request sent. Please check your phone to complete payment.', 'info');
    } catch (error) {
      setMpesaProcessing(false);
      console.error('MPesa Payment Error:', error);
      
      // Extract and display the most useful error message
      let errorMessage = 'M-Pesa payment failed';
      
      if (error.response) {
        console.error('Error Response:', error.response.data);
        errorMessage = error.response.data.error || 'M-Pesa payment failed - server error';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showToast(errorMessage, 'error');
      setLoading(false);
    }
  };
  
  // Process credit card payment
  const processCardPayment = async () => {
    // Validate card details
    if (!cardDetails.cardNumber || !cardDetails.cardHolder || !cardDetails.expiryDate || !cardDetails.cvv) {
      showToast('Please complete all card details', 'error');
      setLoading(false);
      return;
    }
    
    try {
      // Test credit card for development purposes - always succeeds
      const isTestCard = process.env.NODE_ENV === 'development' && 
        cardDetails.cardNumber.replace(/\s/g, '') === '4403933211508236' &&
        cardDetails.cardHolder === 'Edgar Temba' &&
        cardDetails.expiryDate === '08/25' &&
        cardDetails.cvv === '608';
      
      if (isTestCard) {
        console.log('Using test card - payment will always succeed');
      }
      
      // For school admin users, we need to fetch the school ID
      let schoolId = null;
      if (user?.role === 'school_admin') {
        // Fetch the school associated with this admin
        try {
          // First let's try to get the school ID from the user's dashboard data
          const dashboardResponse = await api.get('/schools/dashboard');
          console.log('School dashboard response:', dashboardResponse.data);
          
          if (dashboardResponse.data.success) {
            // Try to extract the school ID from various possible locations
            const schoolData = dashboardResponse.data.data;
            const schoolInfo = schoolData.schoolInfo;
            
            // Try to get the ID from multiple possible fields
            if (schoolInfo && schoolInfo._id) {
              schoolId = schoolInfo._id;
              console.log('Found school ID from schoolInfo._id:', schoolId);
            } else if (schoolInfo && schoolInfo.schoolId) {
              schoolId = schoolInfo.schoolId;
              console.log('Found school ID from schoolInfo.schoolId:', schoolId);
            }
          }
        } catch (error) {
          console.error('Error fetching school dashboard:', error);
          showToast('Could not retrieve your school information', 'error');
          setLoading(false);
          return;
        }
        
        // If we couldn't get the ID, try the profile endpoint
        if (!schoolId) {
          try {
            console.log('Trying profile endpoint as fallback...');
            const profileResponse = await api.get('/schools/profile');
            console.log('School profile response:', profileResponse.data);
            
            // Try to extract the school ID from the profile data
            if (profileResponse.data.success && profileResponse.data.data) {
              // Try to get the ID from multiple possible fields
              if (profileResponse.data.data._id) {
                schoolId = profileResponse.data.data._id;
                console.log('Found school ID from profile _id:', schoolId);
              } else if (profileResponse.data.data.schoolId) {
                schoolId = profileResponse.data.data.schoolId;
                console.log('Found school ID from profile schoolId:', schoolId);
              }
            }
          } catch (profileError) {
            console.error('Error fetching school profile:', profileError);
          }
        }
        
        // If we still couldn't get the ID, show an error
        if (!schoolId) {
          console.error('Could not find school ID in API responses');
          showToast('Could not determine your school information', 'error');
          setLoading(false);
          return;
        }
        
        console.log('Using school ID for subscription:', schoolId);
      }
      
      // In a real implementation, you would integrate with a payment gateway like Stripe
      // For this example, we'll simulate a successful payment
      
      // Create subscription with payment details
      const response = await api.post('/subscriptions', {
        tier: selectedPlan,
        billingCycle,
        paymentMethod: 'card',
        paymentDetails: {
          cardLast4: cardDetails.cardNumber.slice(-4),
          cardType: getCardType(cardDetails.cardNumber),
          isTestCard // Add flag for server to know this is a test card
        },
        discountCode: appliedDiscount ? appliedDiscount.code : null,
        ...(schoolId && { schoolId }) // Include schoolId only if it's available
      });
      
      showToast('Payment successful! Your subscription is now active.', 'success');
      
      // Get appropriate redirect path based on user role
      let redirectPath = '/dashboard';
      if (user) {
        if (user.role === 'parent') {
          redirectPath = '/parent';
        } else if (user.role === 'school_admin') {
          redirectPath = '/school';
        } else if (user.role === 'mentor') {
          redirectPath = '/mentor';
        }
      }
      
      navigate(redirectPath, { state: { subscriptionSuccess: true }});
    } catch (error) {
      console.error('Payment error:', error);
      
      // Extract and display the most useful error message
      let errorMessage = 'Card payment failed';
      
      if (error.response) {
        console.error('Error Response:', error.response.data);
        errorMessage = error.response.data.error || 'Card payment failed - server error';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showToast(errorMessage, 'error');
      setLoading(false);
    }
  };
  
  // Poll for M-Pesa transaction status
  const pollTransactionStatus = async (transactionId) => {
    let attempts = 0;
    const maxAttempts = 20; // Poll for up to ~2 minutes
    
    const checkStatus = async () => {
      if (attempts >= maxAttempts) {
        setMpesaProcessing(false);
        showToast('M-Pesa payment timed out. Please try again', 'error');
        return;
      }
      
      try {
        const response = await api.get(`/mpesa/status/${transactionId}`);
        
        // Check status from response data structure
        const status = response.data.status;
        
        if (status === 'COMPLETED') {
          setMpesaProcessing(false);
          showToast('Payment successful! Your subscription is now active.', 'success');
          
          // Get appropriate redirect path based on user role
          let redirectPath = '/dashboard';
          if (user) {
            if (user.role === 'parent') {
              redirectPath = '/parent';
            } else if (user.role === 'school_admin') {
              redirectPath = '/school';
            } else if (user.role === 'mentor') {
              redirectPath = '/mentor';
            }
          }
          
          navigate(redirectPath, { state: { subscriptionSuccess: true }});
          return;
        } else if (status === 'FAILED') {
          setMpesaProcessing(false);
          showToast('M-Pesa payment failed: ' + (response.data.resultDesc || 'Unknown error'), 'error');
          return;
        }
        
        // If still pending, continue polling
        attempts++;
        setTimeout(checkStatus, 6000); // Check every 6 seconds
      } catch (error) {
        console.error('Error checking transaction status:', error);
        attempts++;
        setTimeout(checkStatus, 6000);
      }
    };
    
    // Start polling
    setTimeout(checkStatus, 8000); // Initial delay to allow for user to enter PIN
  };
  
  // Determine card type based on number
  const getCardType = (number) => {
    // Basic regex patterns for card types
    const patterns = {
      visa: /^4/,
      mastercard: /^5[1-5]/,
      amex: /^3[47]/,
      discover: /^6(?:011|5[0-9]{2})/
    };
    
    for (const [type, pattern] of Object.entries(patterns)) {
      if (pattern.test(number.replace(/\s+/g, ''))) {
        return type;
      }
    }
    
    return 'unknown';
  };
  
  if (!selectedPlan) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <FaSpinner className="animate-spin text-4xl text-primary" />
        <span className="ml-3">Redirecting to subscription page...</span>
      </div>
    );
  }
  
  // Determine the human-readable name for the selected plan
  const getPlanName = () => {
    const plans = user?.role === 'school_admin' 
      ? subscriptionConfig.schoolSubscriptions 
      : subscriptionConfig.parentSubscriptions;
    
    return plans[selectedPlan]?.name || selectedPlan;
  };
  
  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8">
      <Helmet>
        <title>Checkout | Meira</title>
      </Helmet>
      
      <button 
        onClick={() => navigate(-1)} 
        className="mb-6 flex items-center text-gray-600 hover:text-primary transition"
      >
        <FaArrowLeft className="mr-2" /> Back to Subscriptions
      </button>
      
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-6 bg-primary text-white">
          <h1 className="text-2xl font-bold">Complete Your Subscription</h1>
          <p className="mt-2">You're subscribing to our {getPlanName()} plan.</p>
        </div>
        
        <div className="p-6">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Order Summary */}
            <div className="w-full md:w-1/3">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h2 className="text-lg font-semibold mb-4">Order Summary</h2>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Plan:</span>
                    <span className="font-medium">{getPlanName()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Billing:</span>
                    <span className="font-medium">{billingCycle === 'annual' ? 'Annual' : 'Quarterly'}</span>
                  </div>
                  
                  {/* Price Before Discount */}
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>KES {getPrice().toLocaleString()}</span>
                  </div>
                  
                  {/* Discount Display */}
                  {appliedDiscount && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount ({appliedDiscount.percentage}%):</span>
                      <span>- KES {(getPrice() * (appliedDiscount.percentage / 100)).toLocaleString()}</span>
                    </div>
                  )}
                  
                  <div className="border-t my-2"></div>
                  
                  {/* Final Total */}
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total:</span>
                    <span>KES {getFinalPrice().toLocaleString()}</span>
                  </div>
                </div>
              </div>
              
              {/* Discount Code Input */}
              <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">Discount Code</h3>
                {!appliedDiscount ? (
                  <div className="space-y-2">
                    <div className="flex">
                      <input
                        type="text"
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value)}
                        placeholder="Enter discount code"
                        className="w-full px-3 py-2 border border-gray-300 rounded-l-md focus:ring-primary focus:border-primary"
                        disabled={isValidatingCode}
                      />
                      <button
                        onClick={validateDiscountCode}
                        disabled={isValidatingCode || !discountCode.trim()}
                        className="px-4 py-2 bg-primary text-white rounded-r-md hover:bg-primary-dark transition disabled:opacity-50"
                      >
                        {isValidatingCode ? <FaSpinner className="animate-spin" /> : 'Apply'}
                      </button>
                    </div>
                    {discountError && (
                      <p className="text-xs text-red-500">{discountError}</p>
                    )}
                  </div>
                ) : (
                  <div className="bg-green-50 border border-green-200 rounded-md p-3">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        <FaCheckCircle className="text-green-500 mr-2" />
                        <div>
                          <p className="text-sm font-medium">
                            {appliedDiscount.code} ({appliedDiscount.percentage}% off)
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={removeDiscount}
                        className="text-gray-500 hover:text-red-500"
                        title="Remove discount"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-6">
                <h3 className="font-semibold mb-2">Need Help?</h3>
                <p className="text-sm text-gray-600">
                  If you have any questions about your subscription or payment methods, 
                  please contact our support team at <a href="mailto:support@meira.co.ke" className="text-primary">support@meira.co.ke</a>
                </p>
              </div>
            </div>
            
            {/* Payment Methods */}
            <div className="w-full md:w-2/3">
              <h2 className="text-lg font-semibold mb-4">Payment Method</h2>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('mpesa')}
                    className={`py-3 px-4 border rounded-lg flex items-center justify-center ${
                      paymentMethod === 'mpesa' 
                        ? 'border-primary bg-primary bg-opacity-10 text-primary' 
                        : 'border-gray-300 hover:border-primary'
                    }`}
                  >
                    <FaMobile className="mr-2" /> M-Pesa
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('card')}
                    className={`py-3 px-4 border rounded-lg flex items-center justify-center ${
                      paymentMethod === 'card' 
                        ? 'border-primary bg-primary bg-opacity-10 text-primary' 
                        : 'border-gray-300 hover:border-primary'
                    }`}
                  >
                    <FaCreditCard className="mr-2" /> Credit/Debit Card
                  </button>
                </div>
                
                {/* M-Pesa Form */}
                {paymentMethod === 'mpesa' && (
                  <div className="mt-6 space-y-4">
                    <div>
                      <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                        M-Pesa Phone Number
                      </label>
                      <input
                        type="text"
                        id="phoneNumber"
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                        placeholder="e.g. 0712345678 or 254712345678"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                        disabled={mpesaProcessing}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Enter the phone number registered with M-Pesa (formats: 07XX..., 254XXX..., +254XXX...)
                      </p>
                      {phoneNumberError && (
                        <p className="text-xs text-red-500 mt-1">
                          {phoneNumberError}
                        </p>
                      )}
                    </div>
                    
                    {mpesaProcessing && (
                      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 flex items-center">
                        <FaSpinner className="animate-spin text-blue-500 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-blue-800">
                            M-Pesa payment in progress
                          </p>
                          <p className="text-xs text-blue-600">
                            Please check your phone and enter your M-Pesa PIN when prompted
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Credit Card Form */}
                {paymentMethod === 'card' && (
                  <div className="mt-6 space-y-4">
                    <div>
                      <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700 mb-1">
                        Card Number
                      </label>
                      <input
                        type="text"
                        id="cardNumber"
                        name="cardNumber"
                        value={cardDetails.cardNumber}
                        onChange={handleCardNumberChange}
                        placeholder="xxxx xxxx xxxx xxxx"
                        maxLength="19"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="cardHolder" className="block text-sm font-medium text-gray-700 mb-1">
                        Card Holder Name
                      </label>
                      <input
                        type="text"
                        id="cardHolder"
                        name="cardHolder"
                        value={cardDetails.cardHolder}
                        onChange={handleCardInputChange}
                        placeholder="Name as it appears on card"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700 mb-1">
                          Expiry Date
                        </label>
                        <input
                          type="text"
                          id="expiryDate"
                          name="expiryDate"
                          value={cardDetails.expiryDate}
                          onChange={handleExpiryChange}
                          placeholder="MM/YY"
                          maxLength="5"
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="cvv" className="block text-sm font-medium text-gray-700 mb-1">
                          CVV
                        </label>
                        <input
                          type="text"
                          id="cvv"
                          name="cvv"
                          value={cardDetails.cvv}
                          onChange={handleCardInputChange}
                          placeholder="123"
                          maxLength="4"
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                <button
                  onClick={processPayment}
                  disabled={loading || mpesaProcessing}
                  className="w-full mt-6 bg-primary hover:bg-primary-dark text-white py-3 px-4 rounded-md transition focus:ring focus:ring-primary focus:ring-opacity-50 disabled:opacity-70"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <FaSpinner className="animate-spin mr-2" /> Processing...
                    </span>
                  ) : (
                    `Pay KES ${getFinalPrice().toLocaleString()}`
                  )}
                </button>
                
                <p className="text-xs text-center text-gray-500 mt-4">
                  By completing this purchase, you agree to our{' '}
                  <a href="/terms" className="text-primary">Terms of Service</a> and{' '}
                  <a href="/privacy" className="text-primary">Privacy Policy</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout; 